import { create } from 'zustand';
import { Thread, Message, QueryRequest } from '../types';
import { apiService } from '../services/api';
import { toast } from 'react-hot-toast';

interface ChatState {
  // Threads
  threads: Thread[];
  threadsByFolder: Record<string, Thread[]>; // Store threads per folder ID
  currentThread: Thread | null;
  
  // Messages
  messages: Message[];
  
  // Loading states
  isLoadingThreads: boolean;
  isLoadingMessages: boolean;
  isSendingMessage: boolean;
  
  // Error handling
  error: string | null;
}

interface ChatActions {
  // Thread management
  fetchThreads: (noFolder?: boolean, folderId?: string) => Promise<void>;
  getThreadsForFolder: (folderId?: string) => Thread[];
  setCurrentThread: (thread: Thread | null) => void;
  updateThread: (threadId: string, updateData: { title?: string; folder_id?: string | null; skill_id?: string }) => Promise<void>;
  updateThreadSkill: (threadId: string, skillId: string) => Promise<void>;
  deleteThread: (threadId: string) => Promise<void>;
  
  // Message management  
  loadMessages: (threadId: string) => Promise<void>;
  sendMessage: (question: string, threadId?: string, folderId?: string, startNew?: boolean, navigate?: (path: string) => void) => Promise<void>;
  
  // Utility
  clearError: () => void;
  reset: () => void;
}

type ChatStore = ChatState & ChatActions;

export const useChatStore = create<ChatStore>((set, get) => ({
  // Initial state
  threads: [],
  threadsByFolder: {},
  currentThread: null,
  messages: [],
  isLoadingThreads: false,
  isLoadingMessages: false,
  isSendingMessage: false,
  error: null,

  // Fetch threads (unfiled or in specific folder)
  fetchThreads: async (noFolder?: boolean, folderId?: string) => {
    set({ isLoadingThreads: true, error: null });
    try {
      const threads = await apiService.getThreads(noFolder, folderId);
      
      // Store threads in the appropriate location
      if (noFolder) {
        // Unfiled threads go in the main threads array
        set({ threads, isLoadingThreads: false });
      } else if (folderId) {
        // Folder threads go in threadsByFolder
        set(state => ({
          threadsByFolder: {
            ...state.threadsByFolder,
            [folderId]: threads
          },
          isLoadingThreads: false
        }));
      } else {
        // Default: store in main threads array
        set({ threads, isLoadingThreads: false });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to load threads';
      set({ error: message, isLoadingThreads: false });
      toast.error(message);
    }
  },

  // Get threads for a specific folder
  getThreadsForFolder: (folderId?: string) => {
    if (!folderId) {
      return get().threads; // Return unfiled threads
    }
    return get().threadsByFolder[folderId] || [];
  },

  // Set current active thread
  setCurrentThread: (thread: Thread | null) => {
    console.log('🎯 Setting current thread:', thread);
    set({ currentThread: thread });
    if (thread) {
      console.log('📚 Loading messages for thread:', thread.id);
      get().loadMessages(thread.id);
    } else {
      console.log('🧹 Clearing messages (no thread)');
      set({ messages: [] });
    }
  },

  // Load message history for a thread
  loadMessages: async (threadId: string) => {
    console.log('📖 Starting to load messages for thread:', threadId);
    set({ isLoadingMessages: true, error: null });
    try {
      const rawMessages = await apiService.getThreadHistory(threadId);
      console.log('✅ Loaded raw messages:', rawMessages.length, rawMessages);
      
      // Messages now come as individual role-based messages directly from API
      const messages: Message[] = rawMessages.map(rawMsg => ({
        id: rawMsg.id,
        thread_id: rawMsg.thread_id,
        role: rawMsg.role,
        content: rawMsg.content,
        citations: rawMsg.citations || [],
        created_at: rawMsg.created_at,
        response_time: rawMsg.response_time  // Include response_time if present
      }));
      
      console.log('✅ Loaded individual messages:', messages.length, messages);
      set({ messages, isLoadingMessages: false });
      
      // Update thread title if it's still "Loading..." or missing
      const currentThread = get().currentThread;
      if (currentThread && currentThread.id === threadId) {
        if (!currentThread.title || currentThread.title === 'Loading...') {
          try {
            const fullThread = await apiService.getThread(threadId);
            console.log('📝 Updating thread title to:', fullThread.title);
            set({ currentThread: fullThread });
          } catch (error) {
            console.warn('⚠️ Could not fetch thread details:', error);
          }
        }
      }
    } catch (error: any) {
      console.error('❌ Failed to load messages:', error);
      const message = error.response?.data?.message || 'Failed to load messages';
      set({ error: message, isLoadingMessages: false });
      toast.error(message);
    }
  },

  // Send a message (creates thread if needed)
  sendMessage: async (question: string, threadId?: string, folderId?: string, startNew?: boolean, navigate?: (path: string) => void) => {
    console.log('🚀 Sending message:', { question, threadId, folderId, startNew });
    set({ isSendingMessage: true, error: null });
    
    try {
      let finalThreadId = threadId;
      let newThread: Thread | null = null;
      
      
      if (!threadId) {
        console.log('🆕 Creating new thread with question:', question);
        newThread = await apiService.createThread(question, folderId);
        finalThreadId = newThread.id;
        
        console.log('✅ Created new thread:', finalThreadId, 'with title:', newThread.title);
        
        // Update current thread in the store with the proper title from backend
        set({ currentThread: newThread });
        
        // Refresh thread list to show the new thread
        await get().fetchThreads(!folderId, folderId);
        
        // Navigate to the new thread (after setting the current thread)
        if (navigate) {
          navigate(`/chat/${finalThreadId}`);
        }
        
        // Load messages for the new thread to get any existing messages
        await get().loadMessages(finalThreadId);
      }

      // Get current thread to extract skill_id
      const currentThread = get().currentThread;
      const threadSkillId = currentThread?.skill_id;
      
      // Now send the query to the existing thread
      const queryRequest: QueryRequest = {
        question,
        thread_id: finalThreadId!,
        k: 5,
        debug: false,
        ...(threadSkillId && { skill_id: threadSkillId }), // Include skill_id if thread has one
      };

      console.log('📡 API Request:', queryRequest);

      // Send query and get stream
      const stream = await apiService.sendQuery(queryRequest);
      console.log('✅ Got stream response');
      
      // Add user message locally so it appears immediately in the UI
      const userMessage: Message = {
        id: `temp-user-${Date.now()}`,
        thread_id: finalThreadId!,
        role: 'user',
        content: question,
        created_at: new Date().toISOString(),
        citations: [],
      };
      
      set(state => ({
        messages: [...state.messages, userMessage]
      }));
      
      // Add assistant thinking placeholder
      const assistantMessageId = `temp-assistant-${Date.now()}`;
      const assistantMessage: Message = {
        id: assistantMessageId,
        thread_id: finalThreadId!,
        role: 'assistant',
        content: 'Thinking...',
        created_at: new Date().toISOString(),
        citations: [],
      };
      
      set(state => ({
        messages: [...state.messages, assistantMessage]
      }));
      
      // Handle streaming response
      const reader = stream.getReader();
      let finalAnswer = '';
      let citations: any[] = [];
      
      // Add timeout to prevent hanging - increased to 5 minutes for LLM processing
      const streamTimeout = setTimeout(() => {
        console.warn('⚠️ Stream timeout - releasing reader');
        reader.releaseLock();
      }, 300000); // 5 minute timeout for LLM processing
      
      try {
        const decoder = new TextDecoder();
        let buffer = ''; // Buffer to handle partial messages
        
        while (true) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('✅ Stream completed');
            clearTimeout(streamTimeout);
            break;
          }
          
          // Add new chunk to buffer and decode
          buffer += decoder.decode(value, { stream: true });
          console.log('📦 Raw chunk received');
          
          // Process complete lines (end with \n\n for SSE)
          const events = buffer.split('\n\n');
          // Keep the last incomplete part in buffer
          buffer = events.pop() || '';
          
          for (const event of events) {
            if (!event.trim()) continue;
            
            // Handle both JSON events and plain status strings
            if (event.startsWith('data: ')) {
              const jsonStr = event.slice(6).trim();
              if (!jsonStr || jsonStr === '[DONE]') continue;
              
              try {
                const eventData = JSON.parse(jsonStr);
                console.log('📨 Parsed event:', eventData);
                
                // Handle status updates
                if (eventData.event === 'status' && eventData.data) {
                  const statusMessage = eventData.data.message || eventData.data;
                  console.log('📊 Status update:', statusMessage);
                  
                  // Update the assistant message with the status
                  set(state => ({
                    messages: state.messages.map(m => 
                      m.id === assistantMessageId 
                        ? { ...m, content: statusMessage }
                        : m
                    )
                  }));
                }
                
                // Look for answer event
                if (eventData.event === 'answer' && eventData.data) {
                  const answerData = eventData.data;
                  console.log('🎯 Answer data:', answerData);
                  
                  // Handle both null and valid answers
                  if (answerData.answer !== undefined) {
                    finalAnswer = answerData.answer || 'I apologize, but I don\'t have enough information in the knowledge base to answer your question. Please try uploading some documents first.';
                    citations = answerData.sources || answerData.citations || [];
                    const responseTime = answerData.response_time; // Extract response time
                    
                    console.log('✨ Got final answer:', finalAnswer);
                    console.log('📚 Citations:', citations);
                    console.log('⏱️ Response time:', responseTime, 'seconds');
                    
                    // Update assistant message immediately with response time
                    set(state => ({
                      messages: state.messages.map(m => 
                        m.id === assistantMessageId 
                          ? { ...m, content: finalAnswer, citations: citations, response_time: responseTime }
                          : m
                      )
                    }));
                  }
                }
              } catch (parseError) {
                console.warn('⚠️ JSON parse error:', parseError);
              }
            }
          }
        }
      } catch (streamError) {
        console.error('❌ Stream reading error:', streamError);
        clearTimeout(streamTimeout);
        throw streamError;
      } finally {
        clearTimeout(streamTimeout);
        reader.releaseLock();
      }
      
      // Refresh threads to show updated thread
      // If we have folderId, fetch threads in that folder; otherwise fetch unfiled threads
      await get().fetchThreads(!folderId, folderId);
      
      set({ isSendingMessage: false });
      
      if (finalAnswer) {
        // If this was a new thread, poll for title update after a short delay
        if (newThread) {
          console.log('🔄 New thread created, will poll for title update in 3 seconds');
          setTimeout(async () => {
            console.log('🔄 Polling for updated thread...');
            await get().fetchThreads(!folderId, folderId);
            
            // Update currentThread if it's different
            const updatedThread = get().threads.find(t => t.id === finalThreadId);
            if (updatedThread && updatedThread.title !== 'New conversation') {
              console.log(`✅ Title updated: ${updatedThread.title}`);
              get().setCurrentThread(updatedThread);
            }
          }, 3000); // Poll after 3 seconds
        }
      } 
      
    } catch (error: any) {
      console.error('❌ Send message error:', error);
      const message = error.message || 'Failed to send message';
      set({ error: message, isSendingMessage: false });
      toast.error(message);
      
      // Remove the temporary messages on error
      set(state => ({
        messages: state.messages.filter(m => !m.id.startsWith('temp-'))
      }));
    } finally {
      // Ensure sending state is always reset, even if there are unexpected errors
      set({ isSendingMessage: false });
    }
  },

  // Update thread skill
  updateThreadSkill: async (threadId, skillId) => {
    try {
      await get().updateThread(threadId, { skill_id: skillId });
      // No toast - visual feedback handled by SkillSelector component
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update agent';
      toast.error(message);
      throw error;
    }
  },

  // Update thread (rename or move to folder)
  updateThread: async (threadId, updateData) => {
    try {
      const state = get();
      const updatedThread = await apiService.updateThread(threadId, updateData);
      
      // Find the thread to get its current folder
      const currentThread = state.threads.find(t => t.id === threadId) ||
        Object.values(state.threadsByFolder).flat().find(t => t.id === threadId);
      const oldFolderId = currentThread?.folder_id;
      const newFolderId = updateData.folder_id !== undefined ? updateData.folder_id : oldFolderId;
      
      // Update threads in all locations
      const updatedThreadsByFolder: Record<string, Thread[]> = {};
      
      // Update threads in threadsByFolder (remove from old folder, add to new folder if moved)
      Object.keys(state.threadsByFolder).forEach(folderId => {
        if (folderId === oldFolderId && oldFolderId) {
          // Remove from old folder if it had a folder
          const filtered = state.threadsByFolder[folderId].filter(t => t.id !== threadId);
          if (filtered.length > 0) {
            updatedThreadsByFolder[folderId] = filtered;
          }
        } else if (folderId === newFolderId && newFolderId) {
          // Update or add to new folder
          const folderThreads = state.threadsByFolder[folderId] || [];
          const existingIndex = folderThreads.findIndex(t => t.id === threadId);
          if (existingIndex >= 0) {
            updatedThreadsByFolder[folderId] = folderThreads.map(t =>
              t.id === threadId ? { ...t, ...updatedThread } : t
            );
          } else {
            updatedThreadsByFolder[folderId] = [...folderThreads, updatedThread];
          }
        } else {
          // Keep other folders unchanged
          updatedThreadsByFolder[folderId] = state.threadsByFolder[folderId];
        }
      });
      
      // If thread moved to unfiled (no folder), ensure it's in threads array
      let updatedThreads = state.threads;
      if (!newFolderId && oldFolderId) {
        // Moved from folder to unfiled
        updatedThreads = [...state.threads, updatedThread];
      } else if (newFolderId && !oldFolderId) {
        // Moved from unfiled to folder
        updatedThreads = state.threads.filter(t => t.id !== threadId);
      } else if (!newFolderId && !oldFolderId) {
        // Update in unfiled threads
        updatedThreads = state.threads.map(t =>
          t.id === threadId ? { ...t, ...updatedThread } : t
        );
      }
      
      // Update currentThread if it's the one being updated
      const updatedCurrentThread = state.currentThread?.id === threadId 
        ? { ...state.currentThread, ...updatedThread }
        : state.currentThread;
      
      set({
        threads: updatedThreads,
        threadsByFolder: updatedThreadsByFolder,
        currentThread: updatedCurrentThread
      });
      
      // Only show toast if updating title or folder (not skill_id)
      // Skill updates use visual feedback in SkillSelector component
      if (updateData.title !== undefined || updateData.folder_id !== undefined) {
        toast.success('Thread updated successfully!');
      }
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to update thread';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  // Delete thread
  deleteThread: async (threadId) => {
    try {
      await apiService.deleteThread(threadId);
      
      // Remove thread from local state (both threads array and threadsByFolder)
      set(state => {
        const updatedThreadsByFolder: Record<string, Thread[]> = {};
        
        // Remove thread from all folders
        Object.keys(state.threadsByFolder).forEach(folderId => {
          const filtered = state.threadsByFolder[folderId].filter(t => t.id !== threadId);
          if (filtered.length > 0) {
            updatedThreadsByFolder[folderId] = filtered;
          }
        });
        
        return {
          threads: state.threads.filter(thread => thread.id !== threadId),
          threadsByFolder: updatedThreadsByFolder,
          currentThread: state.currentThread?.id === threadId ? null : state.currentThread,
          messages: state.currentThread?.id === threadId ? [] : state.messages
        };
      });
      
      toast.success('Thread deleted successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete thread';
      set({ error: message });
      toast.error(message);
      throw error;
    }
  },

  // Clear error state
  clearError: () => set({ error: null }),
  
  // Reset entire store
  reset: () => set({
    threads: [],
    threadsByFolder: {},
    currentThread: null,
    messages: [],
    isLoadingThreads: false,
    isLoadingMessages: false,
    isSendingMessage: false,
    error: null,
  }),
}));